/**
 * Age Verification Page
 * Initiates PassportReader verification flow
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  ExternalLink,
  ArrowRight,
  ChevronLeft,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgeVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  
  const eventId = searchParams.get('event');
  const attendeeId = searchParams.get('attendee');
  
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  
  // Fetch event requirements and current status
  useEffect(() => {
    const fetchData = async () => {
      if (!session?.access_token || !eventId) {
        setLoading(false);
        return;
      }
      
      try {
        // Get event requirements
        const eventResponse = await fetch(
          `${API_URL}/api/age-verification/event/${eventId}/requirements`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        
        if (eventResponse.ok) {
          const eventData = await eventResponse.json();
          setEventInfo(eventData);
        }
        
        // Get verification status if attendee ID provided
        if (attendeeId) {
          const statusResponse = await fetch(
            `${API_URL}/api/age-verification/status/${attendeeId}`,
            { headers: { Authorization: `Bearer ${session.access_token}` } }
          );
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setVerificationStatus(statusData);
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load verification requirements');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [session?.access_token, eventId, attendeeId]);
  
  // Start verification
  const handleStartVerification = async () => {
    if (!session?.access_token || !attendeeId || !eventId) return;
    
    setStarting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/age-verification/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          event_attendee_id: attendeeId,
          event_id: eventId
        })
      });
      
      const data = await response.json();
      
      if (data.already_verified) {
        setVerificationStatus({ verified: true });
        return;
      }
      
      if (data.success && data.verification_url) {
        // Redirect to PassportReader
        window.location.href = data.verification_url;
      } else {
        setError(data.detail || 'Failed to start verification');
      }
    } catch (err) {
      console.error('Failed to start verification:', err);
      setError('Failed to start verification. Please try again.');
    } finally {
      setStarting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-electric-ice-blue animate-spin mx-auto mb-4" />
          <p className="text-soft-silver">Loading verification...</p>
        </div>
      </div>
    );
  }
  
  if (!eventId || !attendeeId) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="max-w-md bg-graphite/50 border-coral-red/30">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-coral-red mx-auto mb-4" />
            <h1 className="text-xl font-bold text-cloud-white mb-2">Missing Information</h1>
            <p className="text-soft-silver mb-4">
              Event or attendee information is missing. Please try again from your ticket.
            </p>
            <Button onClick={() => navigate('/app/my-tickets')}>
              Go to My Tickets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Already verified
  if (verificationStatus?.verified) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="max-w-md bg-graphite/50 border-cool-mint/30">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 rounded-full bg-cool-mint/10 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-cool-mint" />
            </div>
            <h1 className="text-2xl font-bold text-cloud-white mb-2">Age Verified</h1>
            <p className="text-soft-silver mb-6">
              Your age has been successfully verified for this event.
            </p>
            <Button onClick={() => navigate('/app/my-tickets')}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              View My Tickets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-obsidian" data-testid="age-verification-page">
      {/* Back Link */}
      <div className="p-4">
        <Link 
          to="/app/my-tickets" 
          className="inline-flex items-center gap-2 text-soft-silver hover:text-cloud-white transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back to My Tickets</span>
        </Link>
      </div>
      
      {/* Main Content */}
      <div className="flex items-center justify-center p-6 pt-0">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-electric-ice-blue/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-electric-ice-blue" />
            </div>
            <h1 className="text-2xl font-bold text-cloud-white mb-2">
              Age Verification Required
            </h1>
            {eventInfo && (
              <p className="text-soft-silver">
                {eventInfo.event_name} requires attendees to be {eventInfo.minimum_age}+ years old
              </p>
            )}
          </div>
          
          {/* Error Message */}
          {error && (
            <Card className="mb-6 bg-coral-red/10 border-coral-red/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-coral-red flex-shrink-0" />
                  <p className="text-coral-red text-sm">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Verification Card */}
          <Card className="bg-graphite/50 border-white/[0.08]">
            <CardContent className="p-6">
              {/* How it works */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-cloud-white mb-4">How it works</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-electric-ice-blue/10 flex items-center justify-center flex-shrink-0 text-electric-ice-blue font-bold text-sm">
                      1
                    </div>
                    <div>
                      <p className="text-cloud-white font-medium">Scan your ID</p>
                      <p className="text-soft-silver text-sm">Use your phone camera to scan a valid ID document</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-electric-ice-blue/10 flex items-center justify-center flex-shrink-0 text-electric-ice-blue font-bold text-sm">
                      2
                    </div>
                    <div>
                      <p className="text-cloud-white font-medium">Verify your identity</p>
                      <p className="text-soft-silver text-sm">Take a quick selfie to match your ID photo</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-electric-ice-blue/10 flex items-center justify-center flex-shrink-0 text-electric-ice-blue font-bold text-sm">
                      3
                    </div>
                    <div>
                      <p className="text-cloud-white font-medium">Get verified</p>
                      <p className="text-soft-silver text-sm">Once approved, your ticket will be ready to use</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Privacy Note */}
              <div className="mb-6 p-4 bg-obsidian/50 rounded-xl">
                <p className="text-xs text-muted-text">
                  <strong className="text-soft-silver">Privacy:</strong> We only verify your age. 
                  Your photo and personal details are not stored. Powered by PassportReader.
                </p>
              </div>
              
              {/* Start Button */}
              <Button
                size="lg"
                className="w-full h-14 text-lg bg-electric-ice-blue hover:bg-electric-ice-blue/90 text-obsidian"
                onClick={handleStartVerification}
                disabled={starting}
              >
                {starting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    Start Verification
                    <ExternalLink className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              {/* Retry option if previous verification failed */}
              {verificationStatus?.can_retry && verificationStatus?.status === 'fail' && (
                <p className="text-center text-soft-silver text-sm mt-4">
                  Previous verification failed. Click above to try again.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
